import React, { useState, useEffect } from 'react'
import DateRangeInputCustom from './DateRangeInputCustom'
import { formatStringDate } from '../../config/date'

/**
 * Componente DateRangePicker personalizado con HTML y CSS puro
 * Devuelve fromDate y toDate en formato ISO para usar en consultas
 * 
 * @param {Object} props
 * @param {string} props.fromLabel - Etiqueta para fecha inicio (default: 'Fecha Inicio')
 * @param {string} props.toLabel - Etiqueta para fecha fin (default: 'Fecha Fin')
 * @param {string} props.fromPlaceholder - Placeholder para fecha inicio
 * @param {string} props.toPlaceholder - Placeholder para fecha fin
 * @param {boolean} props.disabled - Si el componente está deshabilitado
 * @param {boolean} props.loading - Si el componente está cargando
 * @param {boolean} props.transparent - Si el componente es transparente
 * @param {boolean} props.error - Si hay error en el componente
 * @param {Date} props.initialFromDate - Fecha inicial para fromDate
 * @param {Date} props.initialToDate - Fecha inicial para toDate
 * @param {function} props.onDateRangeChange - Callback que recibe { fromDate, toDate, fromDateISO, toDateISO }
 */
export default function DateRangePickerCustom({
  fromLabel = 'Fecha Inicio',
  toLabel = 'Fecha Fin',
  fromPlaceholder = 'Fecha Inicio',
  toPlaceholder = 'Fecha Fin',
  disabled = false,
  loading = false,
  transparent = false,
  error = false,
  initialFromDate = null,
  initialToDate = null,
  onDateRangeChange = () => { },
  ...props
}) {
  const [fromDate, setFromDate] = useState(initialFromDate)
  const [toDate, setToDate] = useState(initialToDate)

  // Función para convertir fecha a formato ISO con timezone
  const formatToISO = (date, isEndOfDay = false) => {
    if (!date) return null

    const dateStr = formatStringDate(date, 'yyyy-MM-dd')
    const timeStr = isEndOfDay ? '23:59:59' : '00:00:00'
    return `${dateStr}T${timeStr}-06:00`
  }

  // Función para manejar cambios en fromDate
  const handleFromChange = (newFromDate) => {
    setFromDate(newFromDate)
  }

  // Función para manejar cambios en toDate
  const handleToChange = (newToDate) => {
    setToDate(newToDate)
  }

  // Efecto para notificar cambios al componente padre
  useEffect(() => {
    const fromDateISO = formatToISO(fromDate, false)
    const toDateISO = formatToISO(toDate, true)

    onDateRangeChange({
      fromDate,
      toDate,
      fromDateISO,
      toDateISO,
      // También devuelve las condiciones listas para usar en consultas
      conditions: {
        fromCondition: fromDateISO ? {
          createdAt: {
            gte: fromDateISO
          }
        } : null,
        toCondition: toDateISO ? {
          createdAt: {
            lte: toDateISO
          }
        } : null
      }
    })
  }, [fromDate, toDate])

  return (
    <DateRangeInputCustom
      fromLabel={fromLabel}
      toLabel={toLabel}
      fromPlaceholder={fromPlaceholder}
      toPlaceholder={toPlaceholder}
      disabled={disabled}
      loading={loading}
      transparent={transparent}
      error={error}
      from={fromDate}
      to={toDate}
      onFromChange={handleFromChange}
      onToChange={handleToChange}
      {...props}
    />
  )
}

DateRangePickerCustom.defaultProps = {
  fromLabel: 'Fecha Inicio',
  toLabel: 'Fecha Fin',
  fromPlaceholder: 'Fecha Inicio',
  toPlaceholder: 'Fecha Fin',
  disabled: false,
  loading: false,
  transparent: false,
  error: false,
  initialFromDate: null,
  initialToDate: null,
  onDateRangeChange: () => { },
}
